import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Check, HelpCircle, Link2, Loader2, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useMarketTheme } from '@/hooks/useMarketTheme';
import { useAuth } from '@/contexts/AuthContext';
import { useSeoMeta } from '@/pages/learn/LearnPage';
import StockModal from '@/components/StockModal';
import CompareModal from '@/components/CompareModal';
import LoginModal from '@/components/LoginModal';
import BackOrHomeButton from '@/components/BackOrHomeButton';
import SiteDisclaimer from '@/components/SiteDisclaimer';
import { fetchStockWithFallback } from '@/lib/stockFetch';
import { useReopenModalOnMount } from '@/lib/stockModalRestore';
import TrueReturnPicker, { MAX_TICKERS, SLOT_COLORS } from '@/components/truereturn/TrueReturnPicker';
import TrueReturnChart from '@/components/truereturn/TrueReturnChart';
import TrueReturnStats from '@/components/truereturn/TrueReturnStats';
import TrueReturnSaves from '@/components/truereturn/TrueReturnSaves';
import TrueReturnCombos from '@/components/truereturn/TrueReturnCombos';
import ShareToXButton from '@/components/ShareToXButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const YEARS = [1, 3, 5];
const DEFAULT_TICKERS = ['SCHD', 'VOO'];

function parseSlug(slug) {
  if (!slug) return null;
  const parts = slug.split('-vs-').map((p) => p.toUpperCase()).filter(Boolean);
  return parts.length >= 2 && parts.length <= MAX_TICKERS ? parts : null;
}

function readState(slug, search) {
  const params = new URLSearchParams(search);
  const q = params.get('t');
  const fromQuery = q ? q.toUpperCase().split(',').filter(Boolean).slice(0, MAX_TICKERS) : null;
  const tickers = (fromQuery && fromQuery.length >= 1 ? fromQuery : null) || parseSlug(slug) || DEFAULT_TICKERS;
  const y = Number(params.get('y'));
  const off = (params.get('off') || '').toUpperCase().split(',').filter(Boolean);
  const dripOn = {};
  off.forEach((t) => { dripOn[t] = false; });
  const sc = params.get('s');
  return { tickers, years: YEARS.includes(y) ? y : 5, dripOn, scale: sc === 'log' || sc === 'gap' ? sc : 'linear' };
}

// Remount per route (main vs each combo slug) so BackOrHomeButton re-evaluates
// Back/Home and every combo click is a clean history entry.
export default function TrueReturn() {
  const { combo } = useParams();
  return <TrueReturnPage key={combo || 'main'} combo={combo} />;
}

function TrueReturnPage({ combo }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDark } = useMarketTheme();
  const { user } = useAuth();

  const [initial] = useState(() => readState(combo, location.search));
  // Popular head-to-head click → land with the picker + chart at the top
  // (below the sticky header) instead of the page title.
  const chartAreaRef = useRef(null);
  useEffect(() => {
    if (!location.state?.scrollToChart) return undefined;
    const id = setTimeout(() => {
      // Single-column layouts (phone/tablet) stack the picker above the
      // chart, so target the chart card itself there.
      const stacked = window.innerWidth < 1024;
      const el = stacked ? document.querySelector('[data-testid="true-return-chart-card"]') : chartAreaRef.current;
      if (!el) return;
      const header = document.querySelector('header.sticky');
      const offset = (header ? header.getBoundingClientRect().height : 0) + 8;
      window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - offset, behavior: 'auto' });
    }, 0);
    return () => clearTimeout(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [tickers, setTickers] = useState(initial.tickers);
  const [years, setYears] = useState(initial.years);
  const [scale, setScale] = useState(initial.scale);
  const [dripOn, setDripOn] = useState(initial.dripOn);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [animKey, setAnimKey] = useState(0);
  const reqRef = useRef(0);
  const lastKeyRef = useRef('');

  const [selectedStock, setSelectedStock] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareTicker, setCompareTicker] = useState(null);
  const [loginOpen, setLoginOpen] = useState(false);
  const [watchlist, setWatchlist] = useState([]);

  // Mirror the user's picks into the URL (replace, never push) so the browser
  // Back button returns to exactly the last picks. Combo pages keep a clean
  // slug URL while the picks still match the slug defaults.
  useEffect(() => {
    const slugTickers = parseSlug(combo);
    const off = tickers.filter((t) => dripOn[t] === false);
    const isDefault = slugTickers && slugTickers.join(',') === tickers.join(',') && years === 5 && off.length === 0 && scale === 'linear';
    const params = new URLSearchParams();
    if (!isDefault) {
      params.set('t', tickers.join(','));
      params.set('y', String(years));
      if (off.length) params.set('off', off.join(','));
      if (scale !== 'linear') params.set('s', scale);
    }
    const next = params.toString() ? `?${params.toString()}` : '';
    if (location.search !== next) navigate({ search: next }, { replace: true });
  }, [combo, tickers, years, dripOn, scale, location.search, navigate]);

  const label = tickers.join(' vs ');
  useSeoMeta({
    title: combo
      ? `${label}: True Return Chart — Dividends Reinvested, Inflation-Adjusted | DiviDrip`
      : 'True Return Chart — Growth of $10,000 After Dividends and Inflation | DiviDrip',
    description: combo
      ? `${label} head-to-head: what $10,000 became in today's dollars with dividends reinvested, after CPI-U inflation. Toggle DRIP on/off, switch 1, 3 or 5 years.`
      : 'Compare up to 4 stocks or ETFs on what $10,000 actually became in today\'s purchasing power — price plus reinvested dividends minus CPI-U inflation. 1, 3 and 5 year views.',
    canonical: combo ? `https://twylightcrow.com/true-return/${combo}` : 'https://twylightcrow.com/true-return',
  });
  const shareSlug = tickers.length >= 2 ? tickers.map((t) => t.toLowerCase()).join('-vs-') : null;
  const shareOff = tickers.filter((t) => dripOn[t] === false).join(',');
  const shareExtra = `${shareOff ? `&off=${shareOff}` : ''}${scale !== 'linear' ? `&scale=${scale}` : ''}`;
  const shareQs = years !== 5 || shareExtra ? `?years=${years}${shareExtra}` : '';
  const shareUrl = shareSlug ? `https://twylightcrow.com/api/share/og-page/true-return/${shareSlug}${shareQs}` : '';
  const [copied, setCopied] = useState(false);
  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Chart link copied — paste it anywhere to show the chart card');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt('Copy this link:', shareUrl);
    }
  };
  useEffect(() => {
    if (!shareSlug) return undefined;
    const img = `https://twylightcrow.com/api/true-return/og/${shareSlug}.png?years=${years}${shareExtra}`;
    const sels = ['meta[property="og:image"]', 'meta[property="og:image:secure_url"]', 'meta[name="twitter:image"]', 'meta[property="og:image:width"]', 'meta[property="og:image:height"]', 'meta[name="twitter:card"]'];
    const vals = [img, img, img, '1200', '630', 'summary_large_image'];
    const prev = sels.map((s) => { const el = document.head.querySelector(s); return [el, el?.getAttribute('content')]; });
    prev.forEach(([el], i) => el && el.setAttribute('content', vals[i]));
    return () => prev.forEach(([el, v]) => el && v != null && el.setAttribute('content', v));
  }, [shareSlug, years, shareExtra]);

  useEffect(() => {
    if (tickers.length < 2) { setData(null); lastKeyRef.current = ''; return undefined; }
    const key = `${tickers.join(',')}|${years}`;
    if (lastKeyRef.current === key) return undefined;
    lastKeyRef.current = key;
    const id = ++reqRef.current;
    setLoading(true);
    axios.get(`${API}/true-return`, { params: { tickers: tickers.join(','), years } })
      .then((r) => { if (reqRef.current === id) { setData(r.data); setAnimKey((k) => k + 1); } })
      .catch(() => { if (reqRef.current === id) setData(null); })
      .finally(() => { if (reqRef.current === id) setLoading(false); });
    return undefined;
  }, [tickers, years]);

  useEffect(() => {
    if (!user) { setWatchlist([]); return; }
    axios.get(`${API}/portfolio`, { withCredentials: true }).then((r) => setWatchlist(r.data.map((p) => p.ticker))).catch(() => {});
  }, [user]);

  const toggleWatchlist = async (ticker) => {
    if (!user) { setLoginOpen(true); return; }
    const inList = watchlist.includes(ticker);
    try {
      if (inList) await axios.delete(`${API}/portfolio/${ticker}`, { withCredentials: true });
      else await axios.post(`${API}/portfolio`, { ticker }, { withCredentials: true });
      setWatchlist((w) => (inList ? w.filter((t) => t !== ticker) : [...w, ticker]));
    } catch { /* ignore */ }
  };

  const handleStockClick = async (ticker) => {
    setLoadingDetail(true);
    setModalOpen(true);
    try {
      const d = await fetchStockWithFallback(ticker, { timeoutMs: 3500, onFresh: (fresh) => setSelectedStock(fresh) });
      setSelectedStock(d);
    } catch { setSelectedStock(null); }
    finally { setLoadingDetail(false); }
  };
  useReopenModalOnMount(handleStockClick);

  const addTicker = useCallback((t) => setTickers((cur) => (cur.includes(t) || cur.length >= MAX_TICKERS ? cur : [...cur, t])), []);
  const removeTicker = useCallback((t) => setTickers((cur) => cur.filter((x) => x !== t)), []);
  const toggleDrip = useCallback((t) => setDripOn((d) => ({ ...d, [t]: d[t] === false })), []);
  const loadSave = (t, y) => {
    if (combo) { navigate(`/true-return?t=${t.join(',')}&y=${YEARS.includes(y) ? y : 5}`); return; }
    setTickers(t); if (YEARS.includes(y)) setYears(y);
  };

  const lines = useMemo(() => {
    if (!data?.series) return [];
    return tickers.map((t, i) => {
      const s = data.series[t];
      if (!s || s.error) return null;
      const drip = dripOn[t] !== false;
      return { ticker: t, color: SLOT_COLORS[i], drip, points: drip ? s.drip : s.flat, startDate: s.start_date };
    }).filter(Boolean);
  }, [data, tickers, dripOn]);

  const missing = tickers.filter((t) => data?.series?.[t]?.error);

  return (
    <div className="min-h-screen bg-background" data-testid="true-return-page">
      <div className="w-full max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <BackOrHomeButton testid="true-return-back-or-home-btn" showEscHint={false} />
        <div className="mt-4">
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2" data-testid="true-return-title">
            <TrendingUp className="w-7 h-7 text-sky-500" />
            {combo ? `${label} — True Return Chart` : 'True Return Chart'}
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-3xl" data-testid="true-return-subtitle">
            Price + dividends − inflation = your true return. Each line is what <strong>$10,000</strong> became in <strong>today's purchasing power</strong> (CPI-U adjusted), with dividends reinvested unless you switch Drip off.
          </p>
        </div>

        <div ref={chartAreaRef} className="mt-5 grid grid-cols-1 lg:grid-cols-[minmax(0,340px)_minmax(0,1fr)] gap-4">
          <div>
            <p className="text-base font-bold text-center mb-2" data-testid="true-return-pick-heading">Pick 2 to 4 tickers.</p>
            <TrueReturnPicker
            tickers={tickers}
            series={data?.series}
            dripOn={dripOn}
            onAdd={addTicker}
            onRemove={removeTicker}
            onToggleDrip={toggleDrip}
            onOpenStock={handleStockClick}
            />
          </div>
          <div className="rounded-xl border bg-card p-3 sm:p-4 min-w-0" data-testid="true-return-chart-card">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <h2 className="text-base md:text-lg font-bold">Growth of $10,000 <span className="text-muted-foreground font-normal text-sm">(today's dollars)</span></h2>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="inline-flex items-center rounded-full border bg-muted/40 p-0.5" data-testid="true-return-scale">
                  {[['log', 'Log'], ['gap', `Gap vs ${tickers[0] || 'A'}`]].map(([key, lbl]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setScale((cur) => (cur === key ? 'linear' : key))}
                      aria-pressed={scale === key}
                      className={`rounded-full px-2.5 py-1 text-xs font-semibold transition-colors ${scale === key ? 'bg-violet-600 text-white' : 'text-muted-foreground hover:text-foreground'}`}
                      data-testid={`true-return-scale-${key}`}
                    >
                      {lbl}
                    </button>
                  ))}
                  <Popover>
                    <PopoverTrigger asChild>
                      <button type="button" className="ml-0.5 mr-1 text-muted-foreground hover:text-foreground" aria-label="What do Log and Gap do?" data-testid="true-return-scale-help">
                        <HelpCircle className="w-4 h-4" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="end" className="w-80 text-xs leading-relaxed" data-testid="true-return-scale-help-content">
                      <p className="font-bold mb-1">Seeing lines that sit on top of each other</p>
                      <p><strong>Log</strong> — the $ axis becomes logarithmic, so equal vertical distance = equal <em>percentage</em> move. A 10% move looks the same size at $10K as at $30K, which keeps early years from being squashed flat.</p>
                      <p className="mt-1.5"><strong>Gap vs {tickers[0] || 'A'}</strong> — redraws every line as its <em>dollar lead or lag</em> against the first ticker on the same day. {tickers[0] || 'A'} becomes the flat zero line; a line above it is ahead, below it is behind. Best way to separate near-twins like VOO vs SPY.</p>
                      <p className="mt-1.5 text-muted-foreground">Both are off by default. Click the active one again to return to the normal $ view. The legend under the chart always shows the real $ values.</p>
                    </PopoverContent>
                  </Popover>
                </div>
              <div className="inline-flex rounded-full border bg-muted/40 p-0.5" data-testid="true-return-years">
                {YEARS.map((y) => (
                  <button
                    key={y}
                    type="button"
                    onClick={() => setYears(y)}
                    className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${years === y ? 'bg-sky-600 text-white' : 'text-muted-foreground hover:text-foreground'}`}
                    data-testid={`true-return-years-${y}`}
                  >
                    {y}y
                  </button>
                ))}
              </div>
              {shareSlug && (
                <ShareToXButton
                  variant="button"
                  ticker={`true-return-${shareSlug}`}
                  url={shareUrl}
                  text={`${label} (${years}y${scale === 'gap' ? `, gap vs ${tickers[0]}` : scale === 'log' ? ', log scale' : ''}) — what $10,000 became in today's dollars${shareOff ? ', Drip on/off compared' : ' with dividends reinvested'}, after inflation. True Return Chart on DiviDrip:`}
                  hashtags={['dividends', 'DRIP']}
                  label="Share chart"
                />
              )}
              {shareSlug && (
                <button
                  type="button"
                  onClick={copyShareLink}
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs border transition-colors ${copied ? 'border-emerald-500/60 text-emerald-600 dark:text-emerald-400' : 'border-border hover:border-sky-500/40 hover:text-sky-500'}`}
                  title="Copy a link that unfurls this chart card in Discord, texts and social posts"
                  data-testid="true-return-copy-link"
                >
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Link2 className="w-3.5 h-3.5" />}
                  <span>{copied ? 'Copied' : 'Copy link'}</span>
                </button>
              )}
              </div>
            </div>
            {tickers.length < 2 ? (
              <div className="flex items-center justify-center h-56 text-sm text-muted-foreground" data-testid="true-return-empty">Add a second ticker to draw the comparison.</div>
            ) : loading && !data ? (
              <div className="flex items-center justify-center h-56 text-muted-foreground" data-testid="true-return-loading"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : lines.length >= 1 ? (
              <div className={loading ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
                <TrueReturnChart lines={lines} years={years} animKey={animKey} scale={scale} endAligned={data?.end_aligned} />
              </div>
            ) : (
              <div className="flex items-center justify-center h-56 text-sm text-muted-foreground" data-testid="true-return-nodata">No price history available for this combination.</div>
            )}
            {missing.length > 0 && (
              <p className="mt-2 text-xs text-amber-600 dark:text-amber-400" data-testid="true-return-missing">No price history for {missing.join(', ')} — try another ticker.</p>
            )}
          </div>
        </div>

        {data && tickers.length >= 2 && (
          <div className="mt-4 space-y-4">
            <TrueReturnStats tickers={tickers} series={data.series} dripOn={dripOn} asOf={data.as_of} onOpenStock={handleStockClick} />
            <TrueReturnSaves user={user} tickers={tickers} years={years} onLoad={loadSave} onLoginRequired={() => setLoginOpen(true)} />
          </div>
        )}

        <div className="mt-8">
          <TrueReturnCombos tickers={tickers} />
        </div>

        <p className="mt-6 text-[11px] text-muted-foreground max-w-4xl" data-testid="true-return-method">
          Method: split-adjusted daily closes from Massive; each ex-dividend cash amount is reinvested at that day's close (Drip On) or ignored (Drip Off — price only). Values are deflated by the monthly CPI-U index so every point is in the latest month's dollars. Funds younger than the window start at $10,000 on their first trading day.
        </p>
        <SiteDisclaimer />
      </div>

      <StockModal
        stock={selectedStock}
        open={modalOpen}
        onClose={() => { setModalOpen(false); setSelectedStock(null); }}
        loading={loadingDetail}
        isInWatchlist={selectedStock ? watchlist.includes(selectedStock.ticker) : false}
        onToggleWatchlist={toggleWatchlist}
        isDark={isDark}
        user={user}
        onLoginRequired={() => setLoginOpen(true)}
        onPositionsChanged={() => window.dispatchEvent(new Event('portfolio-changed'))}
        onViewPortfolio={() => { setModalOpen(false); navigate('/?view=portfolio'); }}
        onCompareClick={(t) => { if (t) { setCompareTicker(t); setCompareOpen(true); } }}
        onSelectStock={handleStockClick}
        watchlist={watchlist}
      />
      <CompareModal
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        initialTickerA={compareTicker}
        watchlist={watchlist}
        onTickerClick={() => {}}
      />
      <LoginModal open={loginOpen} onClose={() => setLoginOpen(false)} />
    </div>
  );
}
